import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { api } from '../api/client';
import type { UserRole, UserRoleInfo } from '../types';

interface RoleContextValue {
  role: UserRole;
  hasProjectAssignments: boolean;
  projectIds: number[];
  /** The "people" record name linked to this account (used to match the user
   *  to their sponsor/collaborator rows on projects). Null if no link. */
  personName: string | null;
  loading: boolean;
  isAdmin: boolean;
  isDirection: boolean;
  canSeeTasks: boolean;
  canSeeAllProjects: boolean;
  /** On the proposals allow-list (see server utils/proposals.ts). */
  canSeeProposals: boolean;
  /** Lowercased account name + linked person name — the same identity used
   *  server-side to match task/kanban-card assignee_name for "My Tasks".
   *  Use `myTaskNames.has(assigneeName.toLowerCase())` to tell "my" tasks
   *  apart from everyone else's on a shared board. */
  myTaskNames: Set<string>;
  refreshRole: () => Promise<void>;
}

const RoleContext = createContext<RoleContextValue>({
  role: 'user',
  hasProjectAssignments: false,
  projectIds: [],
  personName: null,
  loading: true,
  isAdmin: false,
  isDirection: false,
  canSeeTasks: false,
  canSeeAllProjects: false,
  canSeeProposals: false,
  myTaskNames: new Set(),
  refreshRole: async () => {},
});

export function RoleProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [roleInfo, setRoleInfo] = useState<UserRoleInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchRole = useCallback(async () => {
    if (!user) {
      setRoleInfo(null);
      setLoading(false);
      return;
    }
    try {
      const info = await api.getRoleInfo() as UserRoleInfo;
      setRoleInfo(info);
    } catch {
      // Fallback for users before migration runs
      setRoleInfo({ role: user.isAdmin ? 'admin' : 'user', hasProjectAssignments: false, projectIds: [], personName: null });
    }
    setLoading(false);
  }, [user]);

  // Wait for AuthContext to actually resolve who's logged in (if anyone)
  // before deciding "no user, default to non-admin". Without this guard,
  // RoleProvider ran fetchRole while `user` was still its initial null (auth
  // check in flight), concluded "logged out", and flipped `loading` to false
  // with isAdmin=false — a false signal any consumer gating on roleLoading
  // (e.g. App.tsx's hash-router) could act on before the real role arrived.
  useEffect(() => {
    if (authLoading) return;
    fetchRole();
  }, [fetchRole, authLoading]);

  const role = roleInfo?.role || 'user';
  const isAdmin = role === 'admin';
  const isDirection = role === 'direction';
  const hasProjectAssignments = roleInfo?.hasProjectAssignments || false;

  const myTaskNames = new Set<string>();
  if (user?.name) myTaskNames.add(user.name.toLowerCase());
  if (roleInfo?.personName) myTaskNames.add(roleInfo.personName.toLowerCase());

  const value: RoleContextValue = {
    role,
    hasProjectAssignments,
    projectIds: roleInfo?.projectIds || [],
    personName: roleInfo?.personName || null,
    loading,
    isAdmin,
    isDirection,
    canSeeTasks: isAdmin || isDirection || hasProjectAssignments,
    canSeeAllProjects: isAdmin || isDirection,
    canSeeProposals: roleInfo?.canSeeProposals || false,
    myTaskNames,
    refreshRole: fetchRole,
  };

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}

export function useRole() {
  return useContext(RoleContext);
}
